import type {
  OrderSummary,
  OrderDetail,
  OrderItem,
  DownloadHistoryItem,
  OrderStatus,
  PurchasedGallerySummary,
  RecoveryAction,
} from '../../domain/entities/Order';
import type {
  OrderSummaryDTO,
  OrderDetailDTO,
  OrderItemDTO,
  DownloadHistoryItemDTO,
  PurchasedGallerySummaryDTO,
} from '../../application/dtos/OrderDTO';

const orderStatuses: OrderStatus[] = ['pending', 'processing', 'completed', 'failed', 'cancelled'];
const downloadStatuses: DownloadHistoryItem['status'][] = [
  'pending',
  'in_progress',
  'completed',
  'failed',
];
const recoveryActions: RecoveryAction[] = ['resume_payment'];

const isOrderStatus = (status: string): status is OrderStatus =>
  orderStatuses.includes(status as OrderStatus);

const isDownloadStatus = (status: string): status is DownloadHistoryItem['status'] =>
  downloadStatuses.includes(status as DownloadHistoryItem['status']);

const isRecoveryAction = (action: string): action is RecoveryAction =>
  recoveryActions.includes(action as RecoveryAction);

export class OrderMapper {
  static toOrderSummary(dto: OrderSummaryDTO): OrderSummary {
    const totalAmount = dto.total ?? dto.total_amount ?? '0.00';
    const itemsCount = dto.items_count ?? dto.items?.length ?? 0;

    return {
      id: dto.id,
      orderNumber: dto.order_number,
      totalAmount,
      status: isOrderStatus(dto.status) ? dto.status : 'pending',
      paymentStatus: dto.payment_status,
      currency: dto.currency,
      itemsCount,
      createdAt: dto.created_at,
      updatedAt: dto.updated_at ?? dto.created_at,
      isRecoverable: dto.is_recoverable ?? false,
      recoverableUntil: dto.recoverable_until ?? null,
      latestPaymentAttempt: dto.latest_payment_attempt
        ? {
            paymentId: dto.latest_payment_attempt.payment_id,
            paymentMethod: dto.latest_payment_attempt.payment_method,
            status: dto.latest_payment_attempt.status,
            createdAt: dto.latest_payment_attempt.created_at,
          }
        : null,
      availableRecoveryActions: (dto.available_recovery_actions ?? []).filter(isRecoveryAction),
    };
  }

  static toOrderDetail(dto: OrderDetailDTO): OrderDetail {
    const totalAmount = dto.total ?? dto.total_amount ?? '0.00';
    const itemsCount = dto.items_count ?? dto.items?.length ?? 0;

    return {
      id: dto.id,
      orderNumber: dto.order_number,
      totalAmount,
      status: isOrderStatus(dto.status) ? dto.status : 'pending',
      paymentStatus: dto.payment_status,
      currency: dto.currency,
      itemsCount,
      createdAt: dto.created_at,
      updatedAt: dto.updated_at ?? dto.created_at,
      isRecoverable: dto.is_recoverable ?? false,
      recoverableUntil: dto.recoverable_until ?? null,
      latestPaymentAttempt: dto.latest_payment_attempt
        ? {
            paymentId: dto.latest_payment_attempt.payment_id,
            paymentMethod: dto.latest_payment_attempt.payment_method,
            status: dto.latest_payment_attempt.status,
            createdAt: dto.latest_payment_attempt.created_at,
          }
        : null,
      availableRecoveryActions: (dto.available_recovery_actions ?? []).filter(isRecoveryAction),
      items: dto.items.map(this.toOrderItem),
      downloadAvailable: dto.download_available,
    };
  }

  static toOrderItem(dto: OrderItemDTO): OrderItem {
    return {
      id: dto.id,
      productName: dto.product_name,
      productPrice: dto.product_price ?? dto.unit_price ?? '0.00',
      productEntityType: dto.product_entity_type || 'unknown',
      productEntityId: dto.product_entity_id ?? '',
      productEntitySlug: dto.product_entity_slug ?? null,
      productEntityCode: dto.product_entity_code ?? null,
      quantity: dto.quantity,
      downloadUrl: dto.download_url ?? null,
      downloadToken: dto.download_token ?? null,
      canDownload: dto.can_download ?? false,
      allowDownload: dto.allow_download ?? false,
    };
  }

  static toDownloadHistoryItem(dto: DownloadHistoryItemDTO): DownloadHistoryItem {
    return {
      id: dto.id,
      orderId: dto.order_id,
      orderItemId: dto.order_item_id,
      filename: dto.filename,
      status: isDownloadStatus(dto.status) ? dto.status : 'pending',
      createdAt: dto.created_at,
      expiresAt: dto.expires_at ?? null,
      downloadUrl: dto.download_url ?? null,
    };
  }

  static toPurchasedGallerySummary(dto: PurchasedGallerySummaryDTO): PurchasedGallerySummary {
    return {
      galleryId: dto.gallery_id,
      galleryCode: dto.gallery_code,
      galleryName: dto.gallery_name,
      slug: dto.slug,
      description: dto.description,
      photoCount: dto.photo_count,
      allowDownload: dto.allow_download,
      purchasedAt: dto.purchased_at,
      orderId: dto.order_id,
      photographerName: dto.photographer_name,
      photographerSlug: dto.photographer_slug,
      categoryName: dto.category_name,
      thumbnailUrl: dto.thumbnail_url,
    };
  }
}
